import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import SummaryFooterRight from '@/components/Summary/SummaryFooterRight'
import SummaryHeaderNav from '@/components/Summary/SummaryHeaderNav'
import SummaryHero from '@/components/Summary/SummaryHero'
import SummaryNextChildren from '@/components/Summary/SummaryNextChildren'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import useTracking from '@/composables/useTracking'
import { CompletionStatus } from '@/state/AppStateMachine'

import SummaryPartDescription from './Part/SummaryPartDescription'
import SummaryPartError from './Part/SummaryPartError'
import SummaryPartKeyPoints from './Part/SummaryPartKeyPoints'
import SummaryPartParallax from './Part/SummaryPartParallax'
import SummaryPartSlider from './Part/SummaryPartSlider'

export default function Summary() {
  const { state, personaConfig, scenario, persona, summary } = useAppState()

  const { trackImpression, trackClick } = useTracking()
  const { t } = useTranslation()

  const summaryScenario = summary.scenario
  const isIntro = state.matches('summary.intro')

  const hasNotStartedScenarios = scenario.status.some((status) => status === CompletionStatus.NOT_STARTED)
  const summaryConfig = personaConfig?.scenarios[summary.scenario]?.summary

  const heroSectionRef = useRef<HTMLDivElement>(null)

  const { gsap } = useGSAP()

  const onClickBackToTop = useCallback(
    (instant = false) => {
      if (!instant)
        trackClick({
          unitName: `${persona}_simulation_${summaryScenario}`,
          unitVariation: 'summary',
          unitLocation: 'tyce',

          clickType: 'button',
          buttonText: 'back_to_top',
        })
      if (heroSectionRef.current) {
        gsap.to(window, {
          scrollTo: { y: heroSectionRef.current.offsetTop },
          duration: instant ? 0 : 1,
          ease: 'power1.inOut',
        })
      }
    },
    [heroSectionRef, gsap, persona, trackClick, summaryScenario]
  )

  useEffect(() => {
    trackImpression({
      unitName: `${persona}_simulation_${summaryScenario}`,
      unitVariation: 'summary',
      unitLocation: null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryScenario])

  useLayoutEffect(() => {
    onClickBackToTop(true)
  }, [summaryScenario, onClickBackToTop])

  const [leaving, setLeaving] = useState<boolean>(false)

  return (
    <article
      data-testid='summary'
      role='group'
      key={summaryScenario}
      className={`Summary col-span-1 col-start-1 row-span-1 row-start-1 relative w-full overflow-hidden`}
    >
      {!isIntro && <SummaryHeaderNav leaving={leaving} />}
      <div className='w-full'>
        <SummaryHero ref={heroSectionRef} />
        {!isIntro && summaryConfig && (
          <>
            {summaryConfig.map((part, i) => {
              const Component = (() => {
                switch (part.type) {
                  case 'description':
                    return SummaryPartDescription
                  case 'parallax':
                    return SummaryPartParallax
                  case 'slider':
                    return SummaryPartSlider
                  case 'key-points':
                    return SummaryPartKeyPoints
                  default:
                    return SummaryPartError
                }
              })()
              return <Component key={i} type={part.type} leaving={leaving} {...part.props} />
            })}

            {hasNotStartedScenarios && (
              <footer className='relative z-2 mt-80 flex justify-center items-center w-full pb-128'>
                <Button variant='secondary' onClick={() => onClickBackToTop()}>
                  {t('summary.backToTop')}
                </Button>
              </footer>
            )}
            {!hasNotStartedScenarios && (
              <SummaryNextChildren
                onClickBackToTop={() => onClickBackToTop()}
                leaving={leaving}
                setLeaving={setLeaving}
              />
            )}
          </>
        )}
      </div>

      <SummaryFooterRight />
    </article>
  )
}
